import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const navRef = useRef(null);
    const location = useLocation();

    // Handle Scroll State
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 100);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Animate Navbar Entrance
    useEffect(() => {
        gsap.fromTo(navRef.current,
            { y: -100, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.5 }
        );
    }, []);

    // Lock body scroll when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isOpen]);

    // All available links
    const allLinks = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Industries', path: '/industries' },
        { name: 'Capabilities', path: '/services' },
        { name: 'Contact', path: '/contact' },
    ];

    // Context-aware navigation: Show all links except the current page
    const navLinks = allLinks.filter(link => link.path !== location.pathname);

    return (
        <header
            ref={navRef}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${isScrolled || isOpen
                ? 'bg-white/80 backdrop-blur-md py-4 border-b border-gray-100 shadow-sm'
                : 'bg-transparent py-6'
                }`}
        >
            <div className="container mx-auto px-6 flex items-center justify-between">
                {/* Logo */}
                <Link to="/" className="group relative z-50 flex items-center gap-3" onClick={() => setIsOpen(false)}>
                    <img
                        src="/logo.png"
                        alt="Prime Solutions Logo"
                        className="h-10 md:h-12 w-auto object-contain"
                        style={{
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }}
                    />
                    <span className="text-xl md:text-2xl font-bold tracking-tight text-[#D6001C] mt-1.5">
                        Prime Solutions
                    </span>
                </Link>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center space-x-8">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#D6001C] ${isScrolled ? 'text-slate-600' : 'text-slate-700'
                                }`}
                        >
                            {link.name}
                        </Link>
                    ))}
                </nav>

                {/* Desktop CTA */}
                <div className="hidden md:block">
                    <Link
                        to="/contact"
                        className={`px-6 py-3 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-300 border ${isScrolled
                            ? 'bg-black text-white hover:bg-[#D6001C] border-black hover:border-transparent'
                            : 'bg-white text-black hover:bg-[#D6001C] hover:text-white border-white hover:border-transparent'
                            }`}
                    >
                        Access
                    </Link>
                </div>

                {/* Mobile Hamburger */}
                <button
                    className="md:hidden relative z-50 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="Toggle menu"
                >
                    <div className="w-6 h-5 flex flex-col justify-between">
                        <span className={`w-full h-0.5 ${isOpen ? 'bg-slate-800' : isScrolled ? 'bg-slate-800' : 'bg-slate-800'} transform transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-2' : ''}`} />
                        <span className={`w-full h-0.5 ${isOpen ? 'bg-slate-800' : isScrolled ? 'bg-slate-800' : 'bg-slate-800'} transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`} />
                        <span className={`w-full h-0.5 ${isOpen ? 'bg-slate-800' : isScrolled ? 'bg-slate-800' : 'bg-slate-800'} transform transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-2.5' : ''}`} />
                    </div>
                </button>
            </div>


            {/* Mobile Menu Overlay */}
            <div
                className={`fixed left-0 right-0 z-40 bg-gradient-to-b from-white via-white to-gray-50 shadow-2xl transition-all duration-500 ease-in-out md:hidden ${isOpen
                    ? 'top-[64px] opacity-100 visible translate-y-0'
                    : 'top-[64px] opacity-0 invisible -translate-y-4 pointer-events-none'
                    }`}
                style={{
                    maxHeight: 'calc(100vh - 64px)',
                    overflowY: 'auto'
                }}
            >
                <div className="container mx-auto px-6 py-8">
                    {/* Menu Links */}
                    <nav className="flex flex-col space-y-1">
                        {allLinks.map((link, index) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={() => setIsOpen(false)}
                                className={`group relative py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-300 ${location.pathname === link.path
                                    ? 'bg-red-50 text-[#D6001C]'
                                    : 'text-slate-700 hover:bg-gray-50 hover:text-[#D6001C]'
                                    }`}
                                style={{
                                    animation: isOpen ? `slideIn 0.4s ease-out ${index * 0.08}s both` : 'none'
                                }}
                            >
                                <span className="relative z-10">{link.name}</span>
                                {location.pathname === link.path && (
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#D6001C] rounded-r-full" />
                                )}
                            </Link>
                        ))}
                    </nav>

                    {/* CTA Button */}
                    <div
                        className="mt-6 pt-6 border-t border-gray-200"
                        style={{
                            animation: isOpen ? `slideIn 0.4s ease-out ${allLinks.length * 0.08}s both` : 'none'
                        }}
                    >
                        <Link
                            to="/contact"
                            onClick={() => setIsOpen(false)}
                            className="block w-full py-4 px-6 text-center rounded-xl bg-gradient-to-r from-[#D6001C] to-[#8B0000] text-white font-bold uppercase tracking-wider shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 hover:scale-[1.02] transition-all duration-300"
                        >
                            Get Access
                        </Link>
                    </div>

                    {/* Decorative Element */}
                    <div className="mt-6 flex items-center justify-center gap-2 opacity-40">
                        <div className="h-px w-12 bg-gradient-to-r from-transparent to-gray-300" />
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                        <div className="h-px w-12 bg-gradient-to-l from-transparent to-gray-300" />
                    </div>
                </div>
            </div>

            {/* Animation keyframes */}
            <style>{`
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateX(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
            `}</style>
        </header>
    );
};

export default Navbar;
